/**
 * Matriz de Permisos por Rol — fuente de verdad **editable** por el ADMIN_PMIS.
 *
 * Esta matriz es la versión visible y mantenible de la matriz de autoridades
 * del VD §3.2 a §3.4. El ADMIN_PMIS puede editarla y los cambios se aplican
 * en vivo al motor `can()`:
 *
 *   - `MATRIZ_TO_PERMS` mapea cada permiso de la matriz a la(s) clave(s) del
 *     PERMS engine (en `src/constants/roles.ts`).
 *   - `construirPermsDesdeMatriz()` construye un override que, vía
 *     `usePermissions`, sobrescribe el default de `PERMS` por cada clave
 *     mapeada. Las claves NO mapeadas (ej. permisos de visualización
 *     transversales) caen al default de `PERMS`.
 *
 * # Modelo de persistencia (3 niveles)
 *
 *   1. `MATRIZ_DEFAULT_FALLBACK` — constante en código, baseline mínima de
 *      fábrica. Sólo se usa la primera vez que el PMIS arranca (cuando todavía
 *      no hay nada en localStorage).
 *   2. `STORAGE_KEY_DEFAULT` ("pmis.matrizPermisosDefault") — el default
 *      "vivo" del PMIS, fijado por el ADMIN_PMIS pulsando
 *      **Establecer como default del PMIS**. Sobrescribe el fallback de
 *      código y es lo que recupera el botón **Restablecer**.
 *   3. `STORAGE_KEY` ("pmis.matrizPermisos") — la matriz activa que aplica
 *      en este momento al motor `can()`. Es la que se edita y guarda con
 *      **Guardar**.
 *
 * Con este modelo el ADMIN_PMIS controla totalmente el ciclo:
 *   editar → Guardar (queda activa) → Establecer como default (queda como
 *   baseline) → Restablecer en cualquier momento vuelve a esa baseline.
 *
 * Cambios en vivo: dispara `MATRIZ_PERMS_EVENT` en window al guardar/restablecer
 * para que `usePermissions` re-evalúe los componentes suscritos.
 */

import type { Role } from "@constants/roles";

import { cargarMatrizRemota, guardarMatrizRemota } from "./matrizStore";

const STORAGE_KEY = "pmis.matrizPermisos";
const STORAGE_KEY_DEFAULT = "pmis.matrizPermisosDefault";

/** Roles que aparecen en la matriz (ADMIN_PMIS se omite por short-circuit). */
export const ROLES_MATRIZ: Role[] = [
  "GTE",
  "JEFE",
  "CFE",
  "PL",
  "PL2",
  "INT",
  "PLANNER",
  "ADOR",
  "ABAS",
  // Roles operativos internos Tecpetrol — Producción/Mantenimiento/Comisionamiento/Almacén.
  // No aprueban procurement; participan en cierre de obra, comisionamiento y entrega de materiales (MR).
  "OPER",
  "MTTO",
  "COMM",
  "ALMACEN",
];

/** Valores frecuentes para una celda — el admin puede personalizar libremente. */
export const VALORES_PRESETS = [
  "SI",
  "NO",
  "propone",
  "revisa",
  "valida",
  "rechaza",
  "0%",
  "≤5%",
  "≤10%",
  "∞",
  "Ejec",
  "Comp",
  "Disc",
  "RD",
  "OT",
  "PR/PO",
] as const;

export interface PermisoMatriz {
  id: string;
  nombre: string;
  /** Valor por rol (subset de Role). */
  valores: Partial<Record<Role, string>>;
}

export interface CategoriaMatriz {
  id: string;
  nombre: string;
  permisos: PermisoMatriz[];
}

export interface MatrizPermisos {
  version: number;
  ultimaModificacion: string; // ISO
  modificadoPor: string;
  categorias: CategoriaMatriz[];
}

/**
 * Baseline de fábrica de la matriz. Sólo se usa cuando el PMIS arranca
 * por primera vez (todavía no hay nada en localStorage). El ADMIN_PMIS
 * puede fijar un default propio en vivo pulsando "Establecer como default
 * del PMIS" — ese default vive en `STORAGE_KEY_DEFAULT` y es lo que recupera
 * el botón "Restablecer". Esta constante es sólo la red de seguridad.
 *
 * Construida según la imagen del usuario y VD §3.2-§3.4.
 */
export const MATRIZ_DEFAULT_FALLBACK: MatrizPermisos = {
  version: 1,
  ultimaModificacion: "2026-05-09T00:00:00Z",
  modificadoPor: "system",
  categorias: [
    {
      id: "afe",
      nombre: "Gestión de AFE",
      permisos: [
        {
          id: "afe.crear",
          nombre: "Crear AFE",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "afe.editar",
          nombre: "Editar AFE (tiempo / costo)",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "afe.aprobar_ampliacion",
          nombre: "Aprobar ampliación AFE",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "propone",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "afe.roles_tecpetrol",
          nombre: "Actualizar roles team Tecpetrol",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "afe.roles_contratistas",
          nombre: "Actualizar roles contratistas",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "SI",
            PLANNER: "SI",
            ADOR: "SI",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "afe.cerrar",
          nombre: "Cerrar AFE",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      id: "contratos",
      nombre: "Contratos Marco",
      permisos: [
        {
          id: "contrato.crear",
          nombre: "Crear / editar Contrato Marco",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "contrato.ver",
          nombre: "Consultar Contratos Marco / tarifas",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      id: "gastos",
      nombre: "Gestión de Gastos AFE",
      permisos: [
        {
          id: "pr.crear",
          nombre: "Crear PR",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "pr.aprobar",
          nombre: "Aprobar PR",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "NO",
            PL: "SI",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "po.crear",
          nombre: "Crear PO",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "po.aprobar",
          nombre: "Aprobar PO",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "NO",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "so.crear",
          nombre: "Crear OT",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          // Aprueban la SO el PL2 y el PL, acotados por su límite económico
          // (fila "Límite OT / PO"): el PL2 sólo aprueba OTs que caben en el
          // saldo disponible del coste; si lo exceden, escala a PL/CFE/JEFE/
          // GTE según el % de sobre-costo. CFE/JEFE/GTE aprueban montos
          // mayores. La autoridad final por monto la resuelve `puedeAprobarSo`.
          id: "so.aprobar_presupuesto",
          nombre: "Aprobar Presupuesto / PDT OT",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "so.limite",
          nombre: "Límite OT / PO",
          valores: {
            "GTE": "∞",
            JEFE: "≤10%",
            CFE: "≤5%",
            PL: "≤5%",
            PL2: "0%",
            INT: "0%",
            PLANNER: "0%",
            ADOR: "0%",
            ABAS: "0%",
            OPER: "0%",
            MTTO: "0%",
            COMM: "0%",
            ALMACEN: "0%",
          },
        },
        {
          // Recursos APU = costos directos, AIU y márgenes — información
          // sensible. Solo personal Tecpetrol; el contratista (PLANNER/ADOR)
          // y la interventoría (INT) NO la ven.
          id: "so.ver_recursos_apu",
          nombre: "Ver Recursos APU (costos directos / AIU)",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      id: "reportes",
      nombre: "Gestión de Reportes",
      permisos: [
        {
          id: "rd.generar",
          nombre: "Generar RD de avance",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "SI",
            PLANNER: "SI",
            ADOR: "SI",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "rd.validar_int",
          nombre: "Validar RD como Interventor (escala al PL)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "SI",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "rd.validar_pl",
          nombre: "Validar RD como PL/PL2 (integra al PMIS)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "rd.bloquear",
          nombre: "Bloquear RD",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "SI",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "rd.integrar",
          nombre: "Integrar RD al PMIS (firma SHA-256)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "eventos.registrar",
          nombre: "Registrar eventos (calidad / HSE / LA / Lean)",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "SI",
            PLANNER: "SI",
            ADOR: "SI",
            ABAS: "NO",
            // OPER/MTTO/COMM registran eventos en su área operativa (HSE, calidad,
            // lecciones aprendidas, comisionamiento). ALMACEN no aplica.
            OPER: "SI",
            MTTO: "SI",
            COMM: "SI",
            ALMACEN: "NO",
          },
        },
        {
          id: "reportes.generar",
          nombre: "Generar reportes",
          valores: {
            "GTE": "Ejec",
            JEFE: "Ejec",
            CFE: "Comp",
            PL: "Comp",
            PL2: "Comp",
            INT: "RD",
            PLANNER: "OT",
            ADOR: "OT",
            ABAS: "PR/PO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      id: "actas",
      nombre: "Actas de Cantidades",
      permisos: [
        {
          id: "acta.generar",
          nombre: "Generar Acta mensual desde RDs",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "SI",
            ADOR: "SI",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "acta.firmar_cont",
          nombre: "Firmar Acta como Contratista",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "SI",
            ADOR: "SI",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "acta.firmar_int",
          nombre: "Firmar Acta como Interventor",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "SI",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "acta.firmar_pl2",
          nombre: "Firmar Acta como PL / PL2",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "acta.facturar",
          nombre: "Marcar Acta como Facturada",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "NO",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "SI",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "acta.anular",
          nombre: "Anular Acta / devolverla a corrección",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      // Gestión de Riesgos del proyecto (PMI PMBOK 7 · ISO 31000 · SAFe 6.0).
      // Módulo exclusivo de Tecpetrol — los contratistas (PLANNER/ADOR) y la
      // interventoría (INT) NO acceden ni siquiera en modo lectura.
      // Detalle en docs/ALCANCE_RIESGOS_STAKEHOLDERS.md.
      id: "riesgos",
      nombre: "Gestión de Riesgos del Proyecto",
      permisos: [
        {
          id: "riesgo.ver",
          nombre: "Consultar Riesgos del proyecto",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "riesgo.crear",
          nombre: "Crear riesgo / identificar amenaza u oportunidad",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "riesgo.editar",
          nombre: "Editar riesgo (evaluación, mitigación, responsable)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          // Aprobación de contingencias = función DRB (Decision Review Board).
          // Decisión D6 del documento de alcance: GTE titular, JEFE como
          // alterno cuando el GTE no está disponible.
          id: "riesgo.aprobar_contingencia",
          nombre: "Aprobar plan de contingencia (función DRB)",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "propone",
            PL: "propone",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      // Gestión de Stakeholders del proyecto (PMI Stakeholder Engagement).
      // Módulo exclusivo de Tecpetrol — el detalle del análisis P×I, el
      // engagement y el sentimiento IA NO se comparten con externos.
      id: "stakeholders",
      nombre: "Gestión de Stakeholders",
      permisos: [
        {
          id: "stakeholder.ver",
          nombre: "Consultar matriz de Stakeholders",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "stakeholder.editar",
          nombre: "Editar stakeholder (necesidad, acción, notas)",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "NO",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          // Crear/eliminar stakeholders globales del proyecto, cambiar
          // poder/interés estructural. JEFE y GTE para evitar drift.
          id: "stakeholder.gestionar",
          nombre: "Crear / eliminar stakeholder global",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          // Sentimiento IA: visible solo a PL/PL2/JEFE (decisión D5).
          // Informativo — el gating está en el componente, no en PERMS.
          id: "stakeholder.ver_sentimiento_ia",
          nombre: "Ver sentimiento IA del stakeholder (D5)",
          valores: {
            "GTE": "NO",
            JEFE: "SI",
            CFE: "NO",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
    {
      // Materiales por Contrato Marco (MR). Pedidos contra contratos marco
      // de MATERIALES con flujo PL2 → ALMACEN → ABAS:
      //   - PL2 crea la MR contra el catálogo de tarifas del CM.
      //   - ALMACEN recibe la mercancía y marca ENTREGADA.
      //   - ABAS registra el pago al proveedor.
      // GTE/JEFE/CFE/PL solo consultan/stats (sin acciones de gestión).
      id: "materiales",
      nombre: "Materiales por Contrato Marco (MR)",
      permisos: [
        {
          id: "mr.crear",
          nombre: "Crear Material Requisition (MR)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "mr.gestionar",
          nombre: "Gestionar entrega de materiales (ALMACEN)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "SI",
          },
        },
        {
          id: "mr.pagar",
          nombre: "Registrar pago de MR al proveedor",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "mr.stats",
          nombre: "Ver estadísticas de MR",
          valores: {
            "GTE": "SI",
            JEFE: "SI",
            CFE: "SI",
            PL: "SI",
            PL2: "SI",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "SI",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "SI",
          },
        },
      ],
    },
    {
      // Configuración del sistema — exclusiva del ADMIN_PMIS por short-circuit
      // (rol técnico). Se incluye en la matriz como REFERENCIA INFORMATIVA
      // para que el GTE/JEFE vean qué cosas controla el administrador del
      // sistema, pero los toggles aquí no afectan al ADMIN (siempre puede).
      id: "configuracion",
      nombre: "Configuración del Sistema",
      permisos: [
        {
          id: "config.notificaciones",
          nombre: "Configurar handovers y plantillas de correo",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "config.distribucion",
          nombre: "Gestionar Lista de distribución de correos",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "config.ia",
          nombre: "Configurar proveedor de IA y API keys",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "config.catalogo_riesgos",
          nombre: "Configurar catálogos de Riesgos (RBS, escalas, umbrales)",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "config.snapshot_default",
          nombre: "Establecer el escenario por defecto del PMIS",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
        {
          id: "config.matriz_autoridades",
          nombre: "Editar esta Matriz de Autoridades",
          valores: {
            "GTE": "NO",
            JEFE: "NO",
            CFE: "NO",
            PL: "NO",
            PL2: "NO",
            INT: "NO",
            PLANNER: "NO",
            ADOR: "NO",
            ABAS: "NO",
            OPER: "NO",
            MTTO: "NO",
            COMM: "NO",
            ALMACEN: "NO",
          },
        },
      ],
    },
  ],
};

// ============================================================================
// Bridge Matriz → motor PERMS (`can()` engine)
// ============================================================================

/** Evento custom disparado al guardar/restablecer la matriz. usePermissions escucha. */
export const MATRIZ_PERMS_EVENT = "pmis:matrizPermisos:changed";

/**
 * Mapeo de permisos de la matriz (id) a claves del motor PERMS.
 * Solo se sobrescriben las claves listadas aquí; el resto cae al default de
 * PERMS (visualizaciones, permisos de detalle, etc.).
 *
 * Algunos permisos de matriz mapean a múltiples claves PERMS porque el motor
 * tiene granularidad mayor (ej. rd.validar cubre rd:approve_pl2 y rd:approve_int).
 */
export const MATRIZ_TO_PERMS: Record<string, readonly string[]> = {
  // Gestión de AFE
  "afe.crear": ["afe:create"],
  "afe.editar": ["afe:edit"],
  "afe.aprobar_ampliacion": ["afe:approve"],
  "afe.cerrar": ["afe:close"],
  "afe.roles_tecpetrol": ["users:manage"],
  // afe.roles_contratistas — no tiene clave PERMS específica, queda solo descriptivo

  // Contratos Marco
  "contrato.crear": ["contract:create", "contract:edit"],
  "contrato.ver": ["contract:view"],

  // Gestión de Gastos AFE
  "pr.crear": ["pr:create", "pr:edit"],
  "pr.aprobar": ["pr:approve"],
  "po.crear": ["po:create", "po:edit"],
  "po.aprobar": ["po:approve"],
  "so.crear": ["so:create", "so:edit"],
  "so.aprobar_presupuesto": ["so:approve"],
  "so.ver_recursos_apu": ["so:ver_recursos_apu"],
  // so.limite — no se mapea aquí porque es un límite económico (no binario);
  // se integra vía `leerLimiteSoPct()` (ver soRules.puedeAprobarSo).

  // Gestión de Reportes
  "rd.generar": ["rd:create", "rd:edit"],
  "rd.validar_int": ["rd:approve_int"],
  "rd.validar_pl": ["rd:approve_pl2"],
  "rd.integrar": ["rd:integrate"],
  // rd.bloquear — no existe en PERMS aún, descriptivo
  "eventos.registrar": ["event:create"],
  // reportes.generar — descriptivo (alcance del reporte: Ejec/Comp/Disc/etc.)

  // Actas de Cantidades
  "acta.generar": ["act:generate"],
  "acta.firmar_cont": ["act:approve_cont"],
  "acta.firmar_int": ["act:approve_int"],
  "acta.firmar_pl2": ["act:approve_pl2"],
  "acta.facturar": ["act:invoice"],
  "acta.anular": ["act:delete"],

  // Configuración del sistema
  "config.snapshot_default": ["pmis:snapshot_default"],

  // Gestión de Riesgos del proyecto (PMI / ISO 31000 / SAFe 6.0).
  "riesgo.ver": ["risk:view"],
  "riesgo.crear": ["risk:create"],
  "riesgo.editar": ["risk:edit"],
  "riesgo.aprobar_contingencia": ["risk:approve"],

  // Materiales por Contrato Marco (MR) — flujo PL2 → ALMACEN → ABAS.
  "mr.crear": ["mr:create"],
  "mr.gestionar": ["mr:gestionar"],
  "mr.pagar": ["mr:pagar"],
  "mr.stats": ["mr:stats"],

  // Gestión de Stakeholders (PMI Stakeholder Engagement).
  "stakeholder.ver": ["stakeholder:view"],
  "stakeholder.editar": ["stakeholder:edit"],
  "stakeholder.gestionar": ["stakeholder:manage"],
  // stakeholder.ver_sentimiento_ia — descriptivo. El gating real está en
  // los componentes (decisión D5); no hay clave PERMS dedicada para no
  // inflar el motor.

  // Configuración del sistema — todas mapean a permisos del ADMIN_PMIS.
  // El ADMIN entra por short-circuit aunque la fila esté en NO; estas
  // entradas existen solo para que el GTE/JEFE vean en la matriz qué
  // controla el administrador del sistema.
  "config.notificaciones": ["notif:admin"],
  "config.ia": ["ia:admin"],
  "config.catalogo_riesgos": ["risk:configure"],
  // config.distribucion — comparte permiso `notif:admin` con config.notificaciones.
  // config.matriz_autoridades — no tiene clave PERMS; el gating se hace por short-circuit ADMIN_PMIS.
};

/**
 * Evalúa si un valor de celda (texto libre) concede el permiso.
 *   - "" / "NO" / "—" → deny
 *   - "propone" / "revisa" → deny (no son la autoridad final)
 *   - cualquier otro valor no vacío ("SI", "valida", "Ejec", "≤5%", "∞", etc.) → grant
 *
 * Esta semántica modela la matriz VD §3.2-§3.4 donde "propone" significa que
 * propone pero no aprueba, "valida" sí valida, y los presets de límite (≤X%)
 * indican autoridad económica acotada que igual concede el permiso.
 */
export function evaluarValorMatriz(valor: string | undefined): boolean {
  const v = (valor ?? "").trim().toUpperCase();
  if (v === "" || v === "NO" || v === "—") return false;
  if (v === "PROPONE" || v === "REVISA") return false;
  return true;
}

/**
 * Construye un override del motor PERMS a partir de la matriz.
 *
 * Devuelve un objeto con las claves PERMS mapeadas por la matriz; cada clave
 * lleva el subconjunto de roles que tienen `evaluarValorMatriz === true`.
 * Las claves no mapeadas se omiten (no sobrescriben el default).
 */
export function construirPermsDesdeMatriz(
  matriz: MatrizPermisos,
): Record<string, Partial<Record<Role, true>>> {
  const result: Record<string, Partial<Record<Role, true>>> = {};
  for (const cat of matriz.categorias) {
    for (const perm of cat.permisos) {
      const permsKeys = MATRIZ_TO_PERMS[perm.id];
      if (!permsKeys) continue;
      const roles: Partial<Record<Role, true>> = {};
      for (const rol of ROLES_MATRIZ) {
        if (evaluarValorMatriz(perm.valores[rol])) {
          roles[rol] = true;
        }
      }
      for (const k of permsKeys) {
        result[k] = roles;
      }
    }
  }
  return result;
}

/** Notifica suscriptores que la matriz cambió. Seguro en SSR (chequea `window`). */
function notificarCambioMatriz(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(MATRIZ_PERMS_EVENT));
  }
}

/**
 * Lee una matriz desde una clave de localStorage. Si no existe o es inválida,
 * devuelve `null` (los consumidores deciden el fallback).
 */
function leerDesdeStorage(key: string): MatrizPermisos | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as MatrizPermisos;
    if (!parsed || !Array.isArray(parsed.categorias)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/**
 * Fusiona una matriz guardada con la baseline de código.
 *
 * Garantiza que toda categoría y permiso definidos en
 * `MATRIZ_DEFAULT_FALLBACK` esté presente en el resultado:
 *   - Si la matriz guardada ya trae la categoría/permiso → mantiene los
 *     valores del admin.
 *   - Si falta una categoría o un permiso nuevo (porque el código añadió
 *     filas tras la última edición del admin) → lo inyecta con los valores
 *     de la baseline.
 *
 * Así, cuando el equipo de desarrollo añade un permiso nuevo al PMIS, el
 * admin lo ve de inmediato en la matriz con su default sensato, sin tener
 * que pulsar "Restablecer".
 */
function fusionarConBaseline(guardada: MatrizPermisos): MatrizPermisos {
  const baseline = MATRIZ_DEFAULT_FALLBACK;
  const catGuardadaPorId = new Map(guardada.categorias.map((c) => [c.id, c]));
  const categoriasFusionadas = baseline.categorias.map((catBase) => {
    const catGuardada = catGuardadaPorId.get(catBase.id);
    if (!catGuardada) return catBase;
    const permGuardadoPorId = new Map(catGuardada.permisos.map((p) => [p.id, p]));
    const permisosFusionados = catBase.permisos.map(
      (pBase) => permGuardadoPorId.get(pBase.id) ?? pBase,
    );
    // Preserva permisos que existían en la guardada y que ya no están en
    // la baseline (deprecados) — el admin puede limpiarlos si quiere.
    const idsBase = new Set(catBase.permisos.map((p) => p.id));
    const huerfanos = catGuardada.permisos.filter((p) => !idsBase.has(p.id));
    return { ...catBase, permisos: [...permisosFusionados, ...huerfanos] };
  });
  // Categorías presentes en la guardada y no en la baseline → conservar.
  const idsBaseCats = new Set(baseline.categorias.map((c) => c.id));
  const catsHuerfanas = guardada.categorias.filter((c) => !idsBaseCats.has(c.id));
  return {
    ...guardada,
    categorias: [...categoriasFusionadas, ...catsHuerfanas],
  };
}

/**
 * Lee el default vivo del PMIS:
 *   1. Si el admin ya fijó un default (STORAGE_KEY_DEFAULT) → ése (fusionado
 *      con la baseline de código para inyectar permisos nuevos).
 *   2. Si no, cae al baseline de código (MATRIZ_DEFAULT_FALLBACK).
 *
 * Esta es la matriz que recupera el botón "Restablecer".
 */
export function leerDefault(): MatrizPermisos {
  const guardada = leerDesdeStorage(STORAGE_KEY_DEFAULT);
  return guardada ? fusionarConBaseline(guardada) : MATRIZ_DEFAULT_FALLBACK;
}

/**
 * Lee la matriz activa del PMIS:
 *   1. Si hay una matriz guardada en STORAGE_KEY → ésa (fusionada con la
 *      baseline para inyectar permisos añadidos al código después de la
 *      última edición del admin).
 *   2. Si no, cae al default vivo (que a su vez cae al fallback de código).
 *
 * Esta es la matriz que aplica al motor `can()`.
 */
export function leerMatriz(): MatrizPermisos {
  const guardada = leerDesdeStorage(STORAGE_KEY);
  return guardada ? fusionarConBaseline(guardada) : leerDefault();
}

/**
 * Parsea un valor de celda de límite de aprobación SO/PO a número:
 *   - "∞"            → Infinity (autoridad ilimitada)
 *   - "≤10%" / "5%"  → ese número
 *   - "0%"           → 0
 *   - "" / "NO" / "—"→ null (el rol no tiene límite/autoridad de aprobación)
 */
export function parseLimitePct(valor: string | undefined): number | null {
  const v = (valor ?? "").trim();
  if (v === "" || v.toUpperCase() === "NO" || v === "—") return null;
  if (v === "∞" || v.toUpperCase() === "INF") return Number.POSITIVE_INFINITY;
  const num = Number(v.replace(/[≤%\s]/g, ""));
  return Number.isFinite(num) ? num : null;
}

/**
 * Lee el límite de aprobación SO/PO de un rol desde la fila `so.limite` de la
 * matriz de permisos activa (editable por el ADMIN_PMIS en Autoridades).
 *
 * El límite es el % de sobre-costo sobre el saldo disponible del coste que el
 * rol puede autorizar (VD §3.3). Devuelve `null` si la fila no existe o el
 * valor no se puede interpretar — los consumidores deciden el fallback.
 *
 * Integra el punto §3.3: la matriz de Autoridades es la fuente de verdad de
 * los límites de aprobación, no un valor hardcodeado en el código.
 */
export function leerLimiteSoPct(role: Role | null): number | null {
  if (!role) return null;
  const matriz = leerMatriz();
  for (const cat of matriz.categorias) {
    const permiso = cat.permisos.find((p) => p.id === "so.limite");
    if (permiso) return parseLimitePct(permiso.valores[role]);
  }
  return null;
}

/**
 * Publica en el backend lo que hay ahora mismo en el espejo local.
 *
 * Se hace SIEMPRE con los dos valores juntos —activa y default— porque viven
 * en una sola fila de configuración: escribir solo uno borraría el otro.
 */
async function publicarMatriz(): Promise<void> {
  const activa = leerDesdeStorage(STORAGE_KEY);
  const porDefecto = leerDesdeStorage(STORAGE_KEY_DEFAULT);
  await guardarMatrizRemota({
    ...(activa ? { activa } : {}),
    ...(porDefecto ? { porDefecto } : {}),
  });
}

/**
 * Trae la matriz compartida y la vuelca al espejo local.
 *
 * Se llama al arrancar la aplicación. Sin esto, cada equipo aplicaría la
 * configuración que tuviera guardada en su propio navegador — que es
 * exactamente el defecto que este módulo corrige.
 *
 * Si no hay backend compartido, o si la lectura falla, se conserva el espejo
 * local: degradar a la configuración del navegador es preferible a dejar la
 * aplicación sin ninguna matriz.
 */
export async function hidratarMatrizDesdeBackend(): Promise<void> {
  const remota = await cargarMatrizRemota();
  if (!remota) return;
  if (remota.activa) localStorage.setItem(STORAGE_KEY, JSON.stringify(remota.activa));
  if (remota.porDefecto) {
    localStorage.setItem(STORAGE_KEY_DEFAULT, JSON.stringify(remota.porDefecto));
  }
  notificarCambioMatriz();
}

/**
 * Persiste la matriz activa y notifica a los suscriptores.
 *
 * El espejo local se escribe primero y de forma síncrona, para que el cambio
 * surta efecto de inmediato en este equipo y `leerMatriz()` pueda seguir
 * siendo síncrona. La publicación al backend va después.
 *
 * Devuelve la promesa de publicación para que la interfaz pueda avisar si
 * falla: el administrador TIENE que enterarse de que su cambio no quedó
 * compartido — si no, creería haber delegado un permiso que solo tiene él.
 */
export function guardarMatriz(m: MatrizPermisos): Promise<void> {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(m));
  notificarCambioMatriz();
  return publicarMatriz();
}

/**
 * Fija la matriz indicada como el nuevo default del PMIS (baseline que
 * recupera "Restablecer"). Persiste también la misma matriz como activa,
 * para que el cambio sea efectivo de inmediato sin requerir un "Guardar"
 * adicional.
 *
 * Devuelve la matriz definitiva (con fecha y autor actualizados).
 */
export function guardarComoDefault(
  m: MatrizPermisos,
  modificadoPor: string,
): MatrizPermisos {
  const nuevoDefault: MatrizPermisos = {
    ...m,
    ultimaModificacion: new Date().toISOString(),
    modificadoPor,
  };
  localStorage.setItem(STORAGE_KEY_DEFAULT, JSON.stringify(nuevoDefault));
  // La matriz activa también queda sincronizada con el nuevo default; ese
  // `guardarMatriz` es el que publica los dos valores al backend.
  void guardarMatriz(nuevoDefault);
  return nuevoDefault;
}

/**
 * Restablece la matriz activa al default vivo del PMIS (el que fijó el
 * ADMIN_PMIS con "Establecer como default"; o, en su defecto, el baseline
 * de código).
 */
export function restablecerMatriz(modificadoPor: string): MatrizPermisos {
  const reset: MatrizPermisos = {
    ...leerDefault(),
    ultimaModificacion: new Date().toISOString(),
    modificadoPor,
  };
  void guardarMatriz(reset);
  return reset;
}

// Alias retrocompatible — código antiguo que importaba MATRIZ_DEFAULT
// sigue funcionando, ahora recibe el default vivo en lugar del fallback.
export const MATRIZ_DEFAULT = MATRIZ_DEFAULT_FALLBACK;
